import { RefObject, useEffect, useMemo, useState } from "react";
import VimeoPlayer, { Options as VimeoOptions } from "@vimeo/player";
import { useHasMounted } from "./useHasMounted";

export type VimeoController = {
  getDuration: () => Promise<number>;
  pause: () => Promise<void>;
  play: () => Promise<void>;
};

export type UseVimeoArgs = {
  videoContainerRef: RefObject<HTMLElement>;
  videoId: string;
  videoHash?: string; // required for private videos
};

export function useVimeo(args: UseVimeoArgs): VimeoController | null {
  const { videoHash, videoId, videoContainerRef } = args;
  const [player, setPlayer] = useState<VimeoPlayer | null>(null);
  // we use hasMounted to make sure this hook (re-)runs after the first render
  // cycle, and the videoContainer is mounted
  const hasMounted = useHasMounted();

  useEffect(() => {
    // initialize the player
    if (hasMounted && player === null) {
      const containerElement = videoContainerRef.current;
      if (!containerElement) {
        return;
      }

      const options: VimeoOptions = {
        responsive: true,
        url: buildUrl(videoId, videoHash),
      };
      // this creates the embedded iframe in the target container, as well as
      // exposes the player api
      const p = new VimeoPlayer(containerElement, options);
      setPlayer(p);
    }
  }, [player, hasMounted, videoContainerRef, videoId, videoHash]);

  const controller = useMemo<VimeoController | null>(() => {
    if (player === null) {
      return null;
    }

    return {
      getDuration: () => player.getDuration(),
      play: () => player.play(),
      pause: () => player.pause(),
    };
  }, [player]);

  return controller;
}

function buildUrl(videoId: string, videoHash?: string): string {
  const hashString = videoHash ? `?h=${videoHash}` : "";
  return `https://player.vimeo.com/video/${videoId}${hashString}`;
}

export default useVimeo;
